import {
  getGridBooleanOperators,
  getGridDateOperators,
  getGridSingleSelectOperators,
  GridColDef,
  GridFilterModel,
  GridFilterOperator,
} from "@mui/x-data-grid-pro";

// Synthetic grid columns for the report-level filters (orgs, dates, currency).
// These drive the report API request rather than filtering the loaded rows,
// but living inside the grid filter model means the filter panel can show and
// edit them, and saved filter presets capture them alongside column filters.
export const ReportFilterField = {
  organisations: `reportOrganisations`,
  startDate: `reportStartDate`,
  endDate: `reportEndDate`,
  currency: `reportCurrency`,
  splitCurrencies: `reportSplitCurrencies`,
} as const;

export const reportFilterFieldList: string[] = Object.values(ReportFilterField);

// The synthetic columns only exist to carry filter state, so keep them out of
// the rendered grid (and out of the columns management panel via
// getTogglableColumns).
export const reportFilterColumnVisibility = Object.fromEntries(
  reportFilterFieldList.map(field => [field, false]),
);

// The report request already applies these filters server-side; a null apply
// fn stops the grid from also trying to filter the loaded rows by them.
function serverSideOperators(operators: GridFilterOperator[]): GridFilterOperator[] {
  return operators.map(operator => ({
    ...operator,
    getApplyFilterFn: () => null,
  }));
}

interface OrgOption {
  value: string;
  label: string;
}

export function buildOrganisationsFilterColumn(orgOptions: OrgOption[]): GridColDef {
  return {
    field: ReportFilterField.organisations,
    headerName: `Entities`,
    type: `singleSelect`,
    valueOptions: orgOptions,
    filterOperators: serverSideOperators(
      getGridSingleSelectOperators().filter(op => op.value === `isAnyOf`),
    ),
  };
}

export function buildDateFilterColumn(field: string, headerName: string): GridColDef {
  return {
    field,
    headerName,
    type: `date`,
    valueGetter: () => null,
    filterOperators: serverSideOperators(
      getGridDateOperators().filter(op => op.value === `is`),
    ),
  };
}

export function buildCurrencyFilterColumn(currencies: string[]): GridColDef {
  return {
    field: ReportFilterField.currency,
    headerName: `Currency`,
    type: `singleSelect`,
    valueOptions: currencies,
    filterOperators: serverSideOperators(
      getGridSingleSelectOperators().filter(op => op.value === `is`),
    ),
  };
}

export function buildSplitCurrenciesFilterColumn(): GridColDef {
  return {
    field: ReportFilterField.splitCurrencies,
    headerName: `Split Currencies`,
    type: `boolean`,
    filterOperators: serverSideOperators(getGridBooleanOperators()),
  };
}

export function getReportFilterValue(model: GridFilterModel, field: string) {
  return model.items.find(item => item.field === field)?.value;
}

// Returns a copy of the model with the item for `field` replaced (or removed
// when the value is empty), leaving all other filter items untouched.
export function withReportFilterValue(
  model: GridFilterModel,
  field: string,
  operator: string,
  value: unknown,
): GridFilterModel {
  const items = model.items.filter(item => item.field !== field);

  const isEmpty = value === null
    || value === undefined
    || value === ``
    || value === false
    || (Array.isArray(value) && value.length === 0);

  if (!isEmpty) {
    items.push({ id: field, field, operator, value });
  }

  return {
    ...model,
    items,
  };
}
